import { useCallback } from "react"
import type { Dashboard, OrganizationDetail, OrganizationSummary, OrganizationUser } from "@/schema/organization-portal"
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"
import { useDevAuth } from "@/hooks/use-dev-auth"

import { organizationFetch } from "./api"

export function useOrganizations() {
  const context = useApiContext()
  return useQuery({
    queryKey: ["organizations"],
    queryFn: async () => apiContextFetch<{ organizations: OrganizationSummary[] }>(await context(), "organizations"),
  })
}
export function useOrganization(id: string) {
  const context = useApiContext()
  return useQuery({
    queryKey: ["organization", id],
    queryFn: async () => apiContextFetch<OrganizationDetail>(await context(), `organizations/${id}`),
    enabled: !!id,
  })
}
export function useOrganizationQuery<T>(organizationId: string, route: string, refresh?: number, enabled = true) {
  const context = useApiContext()
  const bookId = useBookId()
  const { userId } = useDevAuth()
  return useQuery({
    queryKey: ["organization_data", organizationId || "", bookId, route, userId],
    queryFn: async () => organizationFetch<T>(await context(), organizationId, route),
    enabled: enabled && (!!organizationId || !!bookId),
    refetchInterval: refresh,
  })
}
export function useDashboard(organizationId: string, id: string, enabled = true) {
  const context = useApiContext()
  const bookId = useBookId()
  const { userId } = useDevAuth()
  return useQuery({
    queryKey: ["organization_data", organizationId || "", bookId, `dashboards/${id}`, userId],
    queryFn: async () => organizationFetch<Dashboard>(await context(), organizationId, `dashboards/${id}`),
    enabled: enabled && !!id,
    refetchInterval: (query) =>
      [...(query.state.data?.widgets ?? []), ...(query.state.data?.published?.widgets ?? [])].some(
        (widget) => widget.refreshing
      )
        ? 2_000
        : false,
  })
}

export function useDashboardLoadingConfig(organizationId: string, id: string) {
  const bookId = useBookId()
  const { userId } = useDevAuth()
  const client = useQueryClient()
  return client
    .getQueriesData<{ dashboards: Dashboard[] }>({ queryKey: ["organization_data", organizationId || ""] })
    .filter(
      ([key]) =>
        key[4] === userId &&
        (key[3] === "dashboards?scope=accessible" || (key[2] === bookId && key[3] === "dashboards"))
    )
    .flatMap(([, data]) => data?.dashboards ?? [])
    .find((dashboard) => dashboard.id === id)
}

export function useOrganizationUsers(organizationId: string, search: string, enabled = true) {
  const context = useApiContext()
  const select = useCallback(
    (data: { users: OrganizationUser[] }) =>
      data.users.filter((user) =>
        `${user.name} ${user.email} ${user.companies.map((company) => company.company_name).join(" ")}`
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [search]
  )
  return useQuery({
    queryKey: ["organization_users", organizationId],
    queryFn: async () =>
      apiContextFetch<{ users: OrganizationUser[] }>(await context(), `organizations/${organizationId}/users`),
    enabled: enabled && !!organizationId,
    select,
  })
}
